import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import { uuid4 } from '@sentry/utils'
import { AxiosError } from 'axios'
import {
  EnumerationsExportFormat,
  EnumerationsExportStatus,
  EnumerationsExportType,
} from 'client'
import { useAAPSubPlansStore } from './AAPSubPlans'
import { useReportStore } from './report'
import { useNotificationsStore } from './notifications'
import type { TransferObjectsExportAAPReportRequest } from 'client'
import { LABEL, STORE } from '~/constants/strings'
import { AAP_REPORTS_EXPORT_MAX_PERCENT } from '~/constants'
import { HNRV2, fileNetworkRequest } from '~/helpers'
import { openDownloadFileDialogWithData } from '~/helpers/file'
import { notifyFailure, notifySuccess } from '~/utils/notifications'
import { api } from '~/services'

export const OReportBeingExported = {
  AAP_REPORT: 'aap-report',
  PLAN_SUMMARY: 'plan-summary',
  OVERALL_SUMMARY: 'overall-summary',
  BATCH_SUMMARY: 'batch-summary',
  PROJECT_REPORT: 'project-report',
  PROJECT_SUMMARY: 'project-summary',
} as const

export type TReportBeingExportedName =
  (typeof OReportBeingExported)[keyof typeof OReportBeingExported]
const defaultReportBeingExportedType = undefined

interface RootState {
  loading: boolean
  isDownloading: boolean
  exportFormat: number | null
  exportType: number | null
  exportData: {
    buttonLabel: string
    formatOptions: {
      value: EnumerationsExportFormat
      label: string
    }[]
    typeOptions: {
      value: EnumerationsExportType
      label: string
    }[]
    formatModel: EnumerationsExportFormat
    typeModel: EnumerationsExportType
  }
  reportBeingExportedType: TReportBeingExportedName | undefined
  reportBeingExported: {
    notificationID: string
    reportDownloadID: string
    currentStepDescription: string
    failed?: boolean
    percent: number
    exportStatus?: EnumerationsExportStatus
    summaryName: string
    isSummaryExport: boolean
  } | null
  exportDialogOpen: boolean
  runReportsOn: string
  error: boolean
}

export const useAAPExportReportsStore = defineStore(STORE.AAP_EXPORT_REPORTS, {
  state: (): RootState => ({
    loading: false,
    isDownloading: false,
    exportData: {
      buttonLabel: 'Export',
      formatOptions: [
        { value: EnumerationsExportFormat.PDF, label: 'PDF' },
        { value: EnumerationsExportFormat.Excel, label: 'Excel' },
        { value: EnumerationsExportFormat.Word, label: 'Word' },
      ],
      typeOptions: [
        {
          value: EnumerationsExportType.IndividualFiles,
          label: 'Individual Files',
        },
        { value: EnumerationsExportType.CombineAll, label: 'Combine All' },
        {
          value: EnumerationsExportType.CombineFilesPerPlan,
          label: 'Combine Files per Plan',
        },
      ],
      formatModel: 0,
      typeModel: 0,
    },
    exportFormat: null,
    exportType: null,
    reportBeingExportedType: defaultReportBeingExportedType,
    reportBeingExported: null,
    exportDialogOpen: false,
    runReportsOn: LABEL.THIS_PLAN,
    error: false,
  }),
  getters: {
    runReportsOnOptions(): { label: string; value: string }[] {
      const AAPSubPlansStore = useAAPSubPlansStore()
      if (AAPSubPlansStore.savedSelectedSubPlans.length > 0) {
        return [
          {
            label: LABEL.THIS_PLAN,
            value: LABEL.THIS_PLAN,
          },
          {
            label: LABEL.SUB_PLANS,
            value: LABEL.SUB_PLANS,
          },
        ]
      }
      return [
        {
          label: LABEL.THIS_PLAN,
          value: LABEL.THIS_PLAN,
        },
      ]
    },
    selectedPlanIds(): string[] {
      const ReportStore = useReportStore()

      if (!ReportStore.selectedPlan) {
        return []
      }

      if (this.runReportsOn === LABEL.THIS_PLAN) {
        return [ReportStore.selectedPlan?.planID]
      }

      const AAPSubPlansStore = useAAPSubPlansStore()

      return AAPSubPlansStore.savedSelectedSubPlans.map((s) =>
        s.value.toString()
      )
    },
    exportComplete(): boolean {
      return (
        this.reportBeingExported?.percent === AAP_REPORTS_EXPORT_MAX_PERCENT
      )
    },
    NotificationsStore() {
      return useNotificationsStore()
    },
    ReportStore() {
      return useReportStore()
    },
  },
  actions: {
    clearExportData() {
      this.reportBeingExported = null
      this.exportDialogOpen = false
      this.reportBeingExportedType = undefined
    },
    async getExportSettings() {
      try {
        const response = await HNRV2({
          fn: () => api.aapReports.apiAAPReportsExportSettingsGet(),
        })
        if (response instanceof AxiosError) {
          return
        }

        this.exportData.formatModel = response.exportFormatID
        this.exportData.typeModel = response.exportTypeID
      } catch (error) {
        console.error('error', error)
      }
    },
    async onExportSettingsChange() {
      if (this.exportData.formatModel !== EnumerationsExportFormat.PDF) {
        this.exportData.typeModel = EnumerationsExportType.IndividualFiles
      }

      await api.aapReports.apiAAPReportsPost({
        exportFormatID: this.exportData.formatModel,
        exportTypeID: this.exportData.typeModel,
      })
    },
    async retryExport({ notificationID }: { notificationID: string }) {
      const ReportStore = useReportStore()
      ReportStore.loading = true
      this.reportBeingExported = null
      try {
        await api.aapReports.apiAAPReportsExportNotificationIDRetryPost(
          notificationID
        )

        if (this.selectedPlanIds.length === 0) {
          await this.ReportStore.setPlans()
        }
        await this.NotificationsStore.getDropdownNotifications()
        if (this.NotificationsStore.isNotificationPage) {
          await this.NotificationsStore.getNotifications()
        }

        this.exportFormat = this.exportData.formatModel
        this.exportType = this.exportData.typeModel
      } catch (error) {
        console.error('error', error)
      } finally {
        ReportStore.loading = false
      }
    },
    async onExport() {
      const ReportStore = useReportStore()
      ReportStore.loading = true
      this.reportBeingExported = null
      try {
        const reportIDs = ReportStore.currentlyCheckedReports.map(
          (r) => r.aapReport2ClientID
        )
        const planIDs = this.selectedPlanIds

        const payload: TransferObjectsExportAAPReportRequest = {
          format: this.exportData.formatModel,
          type: this.exportData.typeModel,
          aapReport2ClientIDs: reportIDs,
          planIDs,
        }

        const notificationID = await HNRV2({
          fn: () => api.aapReports.apiAAPReportsExportPost(payload),
        })

        if (notificationID instanceof AxiosError) {
          return
        }

        Sentry.captureEvent({
          event_id: uuid4(),
          timestamp: Date.now() / 1000, // number of seconds
          platform: 'javascript',
          // the rest is optional
          level: 'info',
          // transaction: 'export-report',
          message: 'Exporting a report',
        })

        this.reportBeingExportedType = OReportBeingExported.AAP_REPORT
        this.reportBeingExported = {
          notificationID,
        }
        this.exportDialogOpen = true

        if (!this.ReportStore.isAAPSummaries) {
          this.exportFormat = this.exportData.formatModel
          this.exportType = this.exportData.typeModel
        }
      } catch (error) {
        console.error('error', error)
      } finally {
        ReportStore.loading = false
      }
    },
    async onCancelExport(id: string): Promise<void> {
      this.clearExportData()
      const response = await HNRV2({
        fn: () =>
          api.aapReports.apiAAPReportsNotificationIDCancelExportPost(id),
      })

      if (response instanceof AxiosError) {
        return
      }

      notifySuccess('Your export was cancelled')

      const dropdownNotification =
        this.NotificationsStore.dropdownNotifications.find(
          (notification) => notification.notificationID === id
        )
      const pageNotification = this.NotificationsStore.allNotifications.find(
        (notification) => notification.notificationID === id
      )
      if (dropdownNotification) {
        dropdownNotification.exportProgressInfo.exportStatus =
          EnumerationsExportStatus.Cancelled
      }
      if (pageNotification) {
        pageNotification.exportProgressInfo.exportStatus =
          EnumerationsExportStatus.Cancelled
      }
    },
    async onDownloadExport(reportDownloadID: string) {
      try {
        const response = await fileNetworkRequest({
          fn: () =>
            api.aapReportsFile.apiAAPReportsReportDownloadIDDownloadGet(
              reportDownloadID
            ),
        })

        if (response instanceof AxiosError) {
          return
        }

        openDownloadFileDialogWithData({
          response,
        })
      } catch (error) {
        notifyFailure('Unable to download report.')
        console.error('error', error)
      }
    },
    async onRunInBackgroundClick() {
      const NotificationsStore = useNotificationsStore()
      this.clearExportData()
      await NotificationsStore.getDropdownNotifications()
    },
  },
})
